import GreyAppleImg from '../assets/static/GreyApple.png';

import {trimString, preprocessString, isValidUrl, isCaseCountMapUrl} from '../utils/common';

interface RecallDict {
    source: string // Source
    country?: string
    agency: string
    id: string
    uid: string
    alertType?: string
    dateOfRecall: string
    brandName: string
    recallURL?: string
    productDescription: string
    productType: string
    recallReasonDescription: string
    companyName: string
    keywords?: string
    thumbnailURL?: string
    bannerURL?: string
    imageURLs?: string[]
    quickSummary?: string
    recallReasonCategory?: string
    headline?: string
    symptoms?: string
    quantityRecalled?: string
    atRiskGroup?: string
    impactNews?: string
    breakingNews?: string
    breakingNewsSource?: string
    recallStatus?: string
    affectedStates?: string
    mapURL?: string
    caseCountMapURL?: string

    customProps?: { [key: string]: any }
}

export class Recall {
    source: string
    country?: string
    agency: string
    id: string
    uid: string
    alertType?: string
    dateOfRecall: string
    brandName: string
    recallURL?: string
    productDescription: string
    productType: string
    recallReasonDescription: string
    companyName: string
    keywords?: string
    thumbnailURL?: string
    bannerURL?: string
    imageURLs?: string[]
    quickSummary?: string
    recallReasonCategory?: string
    headline?: string
    symptoms?: string
    quantityRecalled?: string
    atRiskGroup?: string
    impactNews?: string
    breakingNews?: string
    breakingNewsSource?: string
    recallStatus?: string
    affectedStates?: string
    mapURL?: string
    caseCountMapURL?: string

    customProps?: { [key: string]: any }

    constructor(dict: RecallDict, source: string) {
        this.source = source
        this.country = dict.country
        this.agency = dict.agency
        this.id = dict.id
        this.uid = dict.uid
        this.alertType = dict.alertType
        this.dateOfRecall = dict.dateOfRecall
        this.brandName = dict.brandName
        this.recallURL = dict.recallURL
        this.productDescription = dict.productDescription
        this.productType = dict.productType
        this.recallReasonDescription = dict.recallReasonDescription
        this.companyName = dict.companyName
        this.keywords = dict.keywords
        this.thumbnailURL = dict.thumbnailURL
        this.bannerURL = dict.bannerURL
        this.imageURLs = dict.imageURLs
        this.quickSummary = dict.quickSummary
        this.recallReasonCategory = dict.recallReasonCategory
        this.headline = dict.headline
        this.symptoms = dict.symptoms
        this.quantityRecalled = dict.quantityRecalled
        this.atRiskGroup = dict.atRiskGroup
        this.impactNews = dict.impactNews
        this.breakingNews = dict.breakingNews
        this.breakingNewsSource = dict.breakingNewsSource
        this.recallStatus = dict.recallStatus
        this.affectedStates = dict.affectedStates
        this.mapURL = dict.mapURL
        this.caseCountMapURL = dict.caseCountMapURL

        this.customProps = dict.customProps
    }

    toDict() {
        return JSON.parse(this.toJsonString());
    }

    toJsonString() {
        return JSON.stringify(this);
    }

    getThumbnailURL() {
        let thumbnailURL = this.thumbnailURL;
        
        if(thumbnailURL && isValidUrl(thumbnailURL)) {
            return thumbnailURL;
        } else {
            //let images = this.imageURLs;
            let images = this.getProductImages(); 
            if (images && images?.length > 0) {
                return images ? images[0] : GreyAppleImg;
            }
        }

        return GreyAppleImg;
    }

    getBannerURL() {
        let bannerURL = this.bannerURL;

        if(bannerURL && isValidUrl(bannerURL)) {
            return bannerURL;
        } else {
            let images = this.getProductImages(); 
            if (images && images?.length > 0) {
                return images ? images[0] : GreyAppleImg;
            }
        }

        return GreyAppleImg;
    }

    getAgency() {
        return this.agency.toUpperCase()
    }

    getAlertType() {
        return this.alertType?.trim() ? this.alertType : "Food Recall";
    }

    hasImages() {
        return (this.getAllImages()?.length !== 0)
    }

    hasProductImages() {
        return (this.getProductImages()?.length !== 0)
    }

    getAllImages() {
        let productImages = this.getProductImages();
        let mapURLs = this.getMapURLs();

        if ( productImages && mapURLs) {
            productImages.push(...mapURLs);
        }

        return productImages;
    }

    getProductImages() {
        let images = this.imageURLs;
        if( images?.length !== 0 ) {
            return images?.filter( (imageURL) => !isCaseCountMapUrl(imageURL) );
        }

        return [];
    }

    hasMaps() {
        return (this.getMapURLs().length !== 0)
    }

    getMapURLs(): string[] {
        let mapURLs = [this.caseCountMapURL ?? "", this.mapURL ?? ""];

        // Filter out undefined values and invalid URLs
        mapURLs = mapURLs.filter( (url) => isValidUrl(url) );

        return mapURLs;
    }

    getProductDescription() {
        return this.productDescription;
    }

    getTileSummaries() {
        return [
            //{name: "ℹ️ QUICK FACTS", summary: this.getHeadline()},
            {name: "📈 SEVERITY", summary: this.getSeverity()},
            {name: "👥 WHO'S AT RISK?", summary: this.getAtRiskGroup()},
            {name: "🤒 SYMPTOMS", summary: this.getSymptoms()}
        ];
    }

    getSeverity() {
        if (trimString(this.impactNews) !== "") {
            return this.impactNews;
        } else if (trimString(this.breakingNews) !== "") {
            return this.breakingNews;
        } else if (trimString(this.quantityRecalled) !== "") {
            return this.quantityRecalled;
        } else {
            return "No information available.";
        }
    }

    getHeadline() {
        if (trimString(this.headline) !== "") {
            return this.headline;
        } else {
        return "No information available.";
        }
    }

    getAtRiskGroup() {
        if (trimString(this.atRiskGroup) !== "") {
            return this.atRiskGroup;
        } else {
        return "No information available.";
        }
    }

    getSymptoms() {
        if (trimString(this.symptoms) !== "") {
            return this.symptoms;
        } else {
        return "No information available.";
        }
    }

    getIds(): string[] {
        return [
            this.uid,
            this.getCompositeId_('/', false),
            this.getCompositeId_('/', true),
            this.getCompositeId_('-', false),
            this.getCompositeId_('-', true)
        ];
    }

    getCountry(): string {
        return (this.country || "us").toLowerCase()
    }

    get compositeId(): string {
        if (this.getCountry() === "us") {
            // ignore cout
            return this.getCompositeId_('/', false)
        } else {
            return this.getCompositeId_('/', true)
        }
    }

    getCompositeId_(s: string = '/', includeCountry: boolean = false): string {
        if (includeCountry) {
            return `${this.getCountry()}${s}${this.getAgency()}${s}${this.id}`.toLowerCase()
        } else {
            return `${this.getAgency()}${s}${this.id}`.toLowerCase()
        }
    }

    isAnnouncement() {
        return this.getAlertType().toLowerCase() === "announcement"
    }

    isMicrobeRecall() {
        return (this.recallReasonCategory === "microbe") && (!this.isPetFoodRecall());
    }

    isPetFoodRecall() {
        if (trimString(this.productType) !== "") {
            let productType = this.productType.toLowerCase()
            return productType.includes("animal") || productType.includes("veterinary") || productType.includes("pet food");
        } else {
            return false;
        }
    }

    isOtherRecall() {
        return !(this.isMicrobeRecall() || this.isPetFoodRecall()); // || (this.recallReasonCategory === "other");
    }

    getRecallTypeEmoji() {
        if (this.isMicrobeRecall()) {
            return "🦠";
        } else if (this.isPetFoodRecall()) {
            return "🐾";
        } else {
            return "💬";
        }
    }

    getRecallReasonDescription(includeEomji: boolean) {
        return (includeEomji) ? `${this.getRecallTypeEmoji()} ${this.recallReasonDescription}` : this.recallReasonDescription;
    }

    getDateString() {
        return this.getDate().toLocaleDateString(); //Return date in format 5/12/2020
    }

    getDate() {
        const recallDate = new Date(this.dateOfRecall);
        return recallDate; //return Date object
        //return Date.parse(this.dateOfRecall); //Returns millisecond since the Unix epoch
    }

    hasBreakingNews() {
        return ( trimString(this.breakingNews) !== "" );
    }

    getBrandName(includeEomji: boolean) {
        return (includeEomji) ? `🏷 ${this.brandName}` : this.brandName;
    }

    getBreakingNews(includeEomji: boolean) {
        return (includeEomji) ? `⚠️ ${this.breakingNews}` : this.breakingNews;
    }

    getBreakingNewsSource() {
        return isValidUrl(this.breakingNewsSource) ? this.breakingNewsSource : this.recallURL;
    }

    getLearnMoreURL() {
        let learnMoreURL = isValidUrl(this.breakingNewsSource) ? this.breakingNewsSource : this.recallURL;

        if (learnMoreURL && window.isAndroid) {
            let url = new URL(learnMoreURL);
            if (window.isAndroid) {
                url.searchParams.append('target', 'android');
                learnMoreURL = url.toString();
            }
        }

        return learnMoreURL
    }

    getRecallURL() {
        let recallURL = this.recallURL;

        if (recallURL && window.isAndroid) {
            let url = new URL(recallURL);
            url.searchParams.append('target', 'android');
            recallURL = url.toString();
        }

        return recallURL
    }

    getAgencyURL() {
        return this.getRecallURL();
    }

    isClosed() {
        return (["terminated", "closed"].includes(trimString(this.recallStatus).toLowerCase()));
    }

    getStatus() {
        return this.isClosed() ? "Closed" : "Active";
    }

    getStatesSummary() {
        let affectedStates = this.affectedStates
        const country = this.getCountry()
        const areaLabelSingular = (country.toLowerCase() === "ca") ? "province" : "state"
        const areaLabelPlural = (country.toLowerCase() === "ca") ? "provinces" : "states"
        const maxAreas = (country.toLowerCase() === "ca") ? 10 : 50

        if (affectedStates) {
            if (affectedStates.toLowerCase() === "nationwide") {
                return "Nationwide"
            } else {
                let count = affectedStates.split(',').length
                count = Math.min(count, maxAreas)

                let stateLabel = (count > 1) ? areaLabelPlural : areaLabelSingular
                return (count !== maxAreas) ? `${count} ${stateLabel}` : "Nationwide"
            }
        }
        return ""
    }

    getSecondaryLabel() {
        if (this.hasBreakingNews()) {
            return this.getBreakingNews(true);
        } else {
            return this.getBrandName(false);
        }
    }

    hasPdfImages() {
        let images = this.imageURLs;

        if( images?.length !== 0 ) {
            return images?.some( (imageURL) => imageURL.includes('.pdf') );
        }

        return false;
    }

    getSearchFriendlyDescription() {
        let headline = this.headline ?? ""
        let productDescription = this.getProductDescription()
        let brandName = this.getBrandName(false)
        let keywords = this.keywords ?? ""
        let recallReason = this.recallReasonDescription ?? ""
        let quickSummary = ( this.getSeverity() ?? "" )
                             + ( this.getAtRiskGroup() ?? "" )
                             + ( this.getSymptoms() ?? "" )
         
        let searchFriendlyDescription = `${headline} ${productDescription} ${keywords} ${brandName} ${recallReason} ${quickSummary}`
        
        return preprocessString(searchFriendlyDescription)
    }

    shouldHideCTAButton() {
        const customProps = this.customProps;

        // Check if hideCTAButton exists and is a boolean
        const hideCTAButton = customProps?.hideCTAButton ?? false;

        // Return true if hideCTAButton is explicitly true, otherwise false
        return hideCTAButton;
    }

    getCTAButtonTitle() {
        const customProps = this.customProps

        const ctaButtonTitle = customProps?.ctaButtonTitle ?? "Learn More";

        return ctaButtonTitle;
    }

    isValidRecall(): boolean {
        return !!this.agency && !!this.id
    }
}

//String.prototype.trim = function() {
//   return this.replace(/^\s+|\s+$/g,'');
//}

export default Recall;